import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Space,
  Switch,
  Table,
  Tooltip,
  Upload,
  message as toast,
} from 'antd';
import type { FormInstance } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { API } from 'service/api-route';
import { colors } from 'theme/tokens';
import { Pill, VerdictPill } from './shared/components';
import { AI_PROVIDER_LABEL, DEFAULT_AI_THRESHOLDS, OPENAI_CATEGORIES } from './shared/labels';
import { FeedAiThresholds, FeedSettingsView, TestImageProviderResult } from './shared/types';
import { feedError, fmtScore, toStringList, uploadFeedMedia } from './shared/utils';
import styles from './styles.module.scss';

/**
 * Phần "Kiểm duyệt AI" của tab Cài đặt (03 §11.5) — gồm cả phần GĐ1b chủ dự án đã duyệt làm ngay
 * (28/09): chọn thứ tự nhà cung cấp, chỉnh ngưỡng, "Thử một ảnh". AI TẮT mặc định; bật cần khoá API
 * trên máy chủ + Quy tắc đăng bài có câu về AI (máy chủ kiểm, 400 TERMS_MISSING_AI_CLAUSE).
 */

// ------------------------------------------------------------------ công tắc có hỏi lại

export const ConfirmSwitch = ({
  id,
  value,
  onChange,
  disabled,
  confirm,
}: {
  id?: string;
  value?: boolean;
  onChange?: (v: boolean) => void;
  disabled?: boolean;
  confirm?: { title: string; content?: string; okText?: string };
}) => (
  <Switch
    // Form.Item truyền id = tên trường: cần để form.scrollToField(...) và nhãn trỏ đúng công tắc.
    id={id}
    checked={!!value}
    disabled={disabled}
    onChange={(next) => {
      if (next && confirm) {
        Modal.confirm({
          title: confirm.title,
          content: confirm.content,
          okText: confirm.okText || 'Bật',
          cancelText: 'Huỷ',
          onOk: () => onChange?.(true),
        });
        return;
      }
      onChange?.(next);
    }}
  />
);

// ------------------------------------------------------------------ nhà cung cấp

const providerNames = (settings: FeedSettingsView): string[] => {
  const names = new Set<string>();
  (settings.aiProviders?.image || []).forEach((n) => names.add(n));
  (settings.aiProviders?.text || []).forEach((n) => names.add(n));
  Object.keys(settings.aiProviders?.enabled || {}).forEach((n) => names.add(n));
  ['openai', 'claude'].forEach((n) => names.add(n));
  return Array.from(names).filter((n) => n && n !== 'none' && n !== 'keyword');
};

const Dot = ({ on }: { on: boolean }) => (
  <span className={styles.statusDot} style={{ background: on ? colors.successInk : colors.textMuted }} />
);

export const ProviderStatus = ({ settings }: { settings: FeedSettingsView }) => {
  const enabled = settings.aiProviders?.enabled || {};
  return (
    <Space size={16} wrap>
      {providerNames(settings).map((name) => (
        <span key={name} style={{ fontSize: 13 }}>
          <Dot on={!!enabled[name]} />
          {AI_PROVIDER_LABEL[name] || name}: {enabled[name] ? 'đã có khoá' : 'chưa có khoá'}
        </span>
      ))}
    </Space>
  );
};

/** Thứ tự thử nhà cung cấp (`aiImageProviders`/`aiTextProviders`): null = theo cấu hình máy chủ. */
const ProviderOrderInput = ({
  value,
  onChange,
  envList,
  settings,
  disabled,
}: {
  value?: string[] | null;
  onChange?: (v: string[] | null) => void;
  envList: string[];
  settings: FeedSettingsView;
  disabled?: boolean;
}) => {
  const enabled = settings.aiProviders?.enabled || {};
  const useEnv = value === null || value === undefined;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <Checkbox
        checked={useEnv}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked ? null : envList.filter((n) => enabled[n]))}
      >
        Theo cấu hình máy chủ: {envList.length ? envList.join(' → ') : '(trống — chưa có AI)'}
      </Checkbox>
      {!useEnv && (
        <Select
          mode="multiple"
          disabled={disabled}
          style={{ maxWidth: 480 }}
          value={value || []}
          placeholder="Chọn nhà cung cấp theo thứ tự thử"
          onChange={(v) => onChange?.(v)}
          options={providerNames(settings).map((name) => ({
            value: name,
            label: `${AI_PROVIDER_LABEL[name] || name}${enabled[name] ? '' : ' — chưa có khoá'}`,
            disabled: !enabled[name],
          }))}
        />
      )}
    </div>
  );
};

// ------------------------------------------------------------------ ngưỡng

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v));

/**
 * Trước khi gửi PUT: mục chặn MẶC ĐỊNH mà không còn trong bảng (máy chủ trả ngưỡng đã trộn, mục đã bỏ
 * thì biến mất) → gửi `null`, nếu không backend (mergeThresholds) sẽ trộn lại giá trị mặc định.
 */
export function thresholdsForSave(value: FeedAiThresholds | null | undefined): FeedAiThresholds | null {
  if (!value) return null;
  const next = clone(value);
  next.openai = next.openai || {};
  const block: Record<string, number | null> = { ...(next.openai.block || {}) };
  Object.keys(DEFAULT_AI_THRESHOLDS.openai.block).forEach((key) => {
    if (block[key] === undefined) block[key] = null;
  });
  next.openai.block = block;
  return next;
}

/** Bảng ngưỡng OpenAI + ô JSON cho nhà cung cấp khác (04 §C2). null = về mặc định khi lưu. */
const ThresholdsInput = ({
  value,
  onChange,
  disabled,
  isDefault,
}: {
  value?: FeedAiThresholds | null;
  onChange?: (v: FeedAiThresholds | null) => void;
  disabled?: boolean;
  isDefault?: boolean;
}) => {
  const effective: FeedAiThresholds = value ? value : (clone(DEFAULT_AI_THRESHOLDS) as FeedAiThresholds);
  const openai = effective.openai || {};
  const others: Record<string, unknown> = {};
  Object.keys(effective).forEach((k) => {
    if (k !== 'openai') others[k] = effective[k];
  });
  const othersText = Object.keys(others).length ? JSON.stringify(others, null, 2) : '';
  const [jsonText, setJsonText] = useState(othersText);
  const [jsonError, setJsonError] = useState<string | null>(null);
  useEffect(() => {
    setJsonText(othersText);
    setJsonError(null);
  }, [othersText]);

  const setBlock = (key: string, v: number | null) => {
    const next = clone(effective);
    next.openai = next.openai || {};
    const block = { ...(next.openai.block || {}) };
    // null (không xoá khoá): backend mergeThresholds coi null = bỏ chặn mục này; thiếu khoá = dùng mặc định.
    block[key] = v === null || v === undefined ? null : v;
    next.openai.block = block;
    onChange?.(next);
  };
  const setReview = (v: number | null) => {
    const next = clone(effective);
    next.openai = next.openai || {};
    if (v === null || v === undefined) delete next.openai.review;
    else next.openai.review = v;
    onChange?.(next);
  };
  const applyJson = () => {
    const raw = jsonText.trim();
    if (!raw) {
      onChange?.({ openai: clone(openai) } as FeedAiThresholds);
      setJsonError(null);
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('not object');
      onChange?.({ ...parsed, openai: clone(openai) } as FeedAiThresholds);
      setJsonError(null);
    } catch {
      setJsonError('JSON không hợp lệ — chưa áp dụng.');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Space wrap>
        {value === null ? (
          <Pill color={colors.infoInk} bg={colors.infoSoft}>
            Về mặc định khi lưu
          </Pill>
        ) : isDefault ? (
          <Pill color={colors.textSecondary} bg={colors.surfaceMuted}>
            Mặc định
          </Pill>
        ) : (
          <Pill color={colors.warningInk} bg={colors.warningSoft}>
            Đã chỉnh
          </Pill>
        )}
        <Button size="small" disabled={disabled || value === null} onClick={() => onChange?.(null)}>
          Về mặc định
        </Button>
      </Space>
      <div className="custom-table">
        <Table
          size="small"
          rowKey="key"
          pagination={false}
          dataSource={OPENAI_CATEGORIES}
          scroll={{ x: 520 }}
          columns={[
            {
              title: 'Mục (OpenAI)',
              key: 'label',
              render: (_, c) => (
                <span>
                  {c.label} <span className={styles.muted}>{c.key}</span>
                </span>
              ),
            },
            {
              title: 'Chặn khi điểm ≥',
              key: 'block',
              width: 160,
              render: (_, c) => (
                <InputNumber
                  size="small"
                  min={0}
                  max={1}
                  step={0.05}
                  disabled={disabled}
                  placeholder="không chặn"
                  value={(openai.block || {})[c.key] ?? null}
                  onChange={(v) => setBlock(c.key, typeof v === 'number' ? v : null)}
                  style={{ width: 130 }}
                />
              ),
            },
          ]}
        />
      </div>
      <Space wrap>
        <span>Cần xem khi bất kỳ điểm nào ≥</span>
        <InputNumber
          size="small"
          min={0}
          max={1}
          step={0.05}
          disabled={disabled}
          value={typeof openai.review === 'number' ? openai.review : null}
          onChange={(v) => setReview(typeof v === 'number' ? v : null)}
          style={{ width: 110 }}
        />
      </Space>
      <div>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Nhà cung cấp khác (JSON, vd. claude)</div>
        <Input.TextArea
          className={styles.words}
          disabled={disabled}
          value={jsonText}
          onChange={(e) => setJsonText(e.target.value)}
          onBlur={applyJson}
          autoSize={{ minRows: 2, maxRows: 8 }}
          placeholder='{ "claude": { "blockMinScore": 0 } }'
        />
        {jsonError && <div style={{ color: colors.dangerInk, fontSize: 12, marginTop: 4 }}>{jsonError}</div>}
      </div>
      <div className={styles.hint}>
        Điểm từ 0 đến 1. Để trống ô "Chặn" = không chặn theo mục đó. Mặc định: tình dục liên quan trẻ vị thành niên
        0,3; tình dục 0,9; bạo lực ghê rợn 0,9; cần xem 0,5. Claude tự trả kết quả; "blockMinScore" chỉ hạ "chặn"
        xuống "cần xem" khi điểm thấp hơn số này (mặc định 0).
      </div>
    </div>
  );
};

// ------------------------------------------------------------------ "Thử một ảnh" (D29)

function parseTestImage(res: any): TestImageProviderResult[] {
  const body = res?.results ?? res?.providers ?? res?.data ?? res;
  const list: any[] = Array.isArray(body)
    ? body
    : body && typeof body === 'object'
    ? Object.keys(body).map((k) => ({ provider: k, ...(body[k] || {}) }))
    : [];
  return list.map((r) => ({
    provider: String(r?.provider || r?.name || '?'),
    verdict: r?.verdict ?? null,
    labels: toStringList(r?.labels),
    score: typeof r?.score === 'number' ? r.score : null,
    latencyMs: r?.latencyMs ?? r?.latency_ms ?? null,
    error: r?.error || r?.errorMessage || null,
  }));
}

const TestImageBox = () => {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [results, setResults] = useState<TestImageProviderResult[] | null>(null);

  useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview);
    },
    [preview]
  );

  const run = async (file: File) => {
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
      toast.error('Chỉ thử được ảnh JPG, PNG hoặc WEBP.');
      return;
    }
    setPreview(URL.createObjectURL(file));
    setResults(null);
    setLoading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const data = await uploadFeedMedia(API.feedAdminTestImage, form);
      setResults(parseTestImage(data));
    } catch (err) {
      toast.error(feedError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.box}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>Thử một ảnh</div>
      <div className={styles.hint} style={{ marginBottom: 8 }}>
        Gửi 1 ảnh tới các nhà cung cấp AI đang cấu hình để xem kết quả trước khi bật. Ảnh thử không được lưu. Cần
        khoá API trên máy chủ.
      </div>
      <Space align="start" wrap>
        <Upload
          accept="image/jpeg,image/png,image/webp"
          showUploadList={false}
          maxCount={1}
          beforeUpload={(file) => {
            run(file as File);
            return false;
          }}
        >
          <Button icon={<UploadOutlined />} loading={loading}>
            Chọn ảnh để thử
          </Button>
        </Upload>
        {preview && (
          <img src={preview} alt="" style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 8 }} />
        )}
      </Space>
      {results && (
        <div className="custom-table" style={{ marginTop: 10 }}>
          <Table<TestImageProviderResult>
            size="small"
            rowKey="provider"
            pagination={false}
            dataSource={results}
            locale={{ emptyText: 'Máy chủ không trả kết quả nào (chưa có nhà cung cấp AI).' }}
            scroll={{ x: 600 }}
            columns={[
              { title: 'Nhà cung cấp', key: 'p', width: 140, render: (_, r) => AI_PROVIDER_LABEL[r.provider] || r.provider },
              {
                title: 'Kết quả',
                key: 'v',
                width: 110,
                render: (_, r) =>
                  r.error ? (
                    <span style={{ color: colors.dangerInk }}>{r.error}</span>
                  ) : (
                    <VerdictPill verdict={r.verdict ? String(r.verdict).toLowerCase() : null} />
                  ),
              },
              { title: 'Nhãn', key: 'l', render: (_, r) => (r.labels.length ? r.labels.join(', ') : '—') },
              { title: 'Điểm', key: 's', width: 80, render: (_, r) => fmtScore(r.score) || '—' },
              { title: 'Thời gian (ms)', key: 't', width: 120, render: (_, r) => r.latencyMs ?? '—' },
            ]}
          />
        </div>
      )}
    </div>
  );
};

// ------------------------------------------------------------------ phần AI

const AI_CONFIRM = {
  title: 'Bật kiểm duyệt AI?',
  content:
    'Bật AI sẽ tạo phiên bản Quy tắc mới có câu về AI — mọi người phải bấm Đồng ý lại trước khi đăng/bình luận tiếp. Nội dung của người chưa đồng ý sẽ không được gửi đi soi.',
  okText: 'Bật',
};

const NO_PROVIDER_TIP = 'Máy chủ chưa cấu hình nhà cung cấp AI (biến MODERATION_*_PROVIDERS và khoá API).';

export const AiSection = ({
  form,
  settings,
  readOnly,
  isAdmin,
}: {
  form: FormInstance;
  settings: FeedSettingsView;
  readOnly: boolean;
  isAdmin: boolean;
}) => {
  const imageProviders = Form.useWatch('aiImageProviders', form) as string[] | null | undefined;
  const textProviders = Form.useWatch('aiTextProviders', form) as string[] | null | undefined;
  const termsText = Form.useWatch('termsText', form) as string | null | undefined;
  const termsHasAiClause = Form.useWatch('termsHasAiClause', form) as boolean | undefined;
  const aiImagesEnabled = Form.useWatch('aiImagesEnabled', form) as boolean | undefined;
  const aiTextEnabled = Form.useWatch('aiTextEnabled', form) as boolean | undefined;
  const aiCommentsEnabled = Form.useWatch('aiCommentsEnabled', form) as boolean | undefined;

  const enabled = settings.aiProviders?.enabled || {};
  const envImage = settings.aiProviders?.image || [];
  const envText = settings.aiProviders?.text || [];
  const imageChain = imageProviders ?? envImage;
  const textChain = textProviders ?? envText;
  const imageOk = imageChain.some((p) => enabled[p]);
  const textOk = textChain.some((p) => enabled[p]);
  const anyOn = !!(aiImagesEnabled || aiTextEnabled || aiCommentsEnabled);
  const usingDefaultTerms = termsText === null || termsText === undefined;

  const switchItem = (name: string, label: string, ok: boolean, current?: boolean) => {
    const off = !ok && !current;
    const control = (
      <Form.Item name={name} noStyle>
        <ConfirmSwitch disabled={readOnly || off} confirm={AI_CONFIRM} />
      </Form.Item>
    );
    return (
      <Form.Item label={label}>
        {off ? (
          <Tooltip title={NO_PROVIDER_TIP}>
            <span>{control}</span>
          </Tooltip>
        ) : (
          control
        )}
      </Form.Item>
    );
  };

  return (
    <div>
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="Bật AI nghĩa là chữ, ảnh và khung hình video được gửi tới dịch vụ AI ở Mỹ (OpenAI/Anthropic) để soi."
        description="Trước khi bật: thêm câu về AI vào Điều khoản đăng bài (phần Điều khoản) và hỏi luật sư về hồ sơ chuyển dữ liệu ra nước ngoài (Luật 91/2025). Từ cấm luôn chạy, không cần AI."
      />
      <div className={styles.box} style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>
          Trạng thái:{' '}
          {anyOn ? (
            <span style={{ color: colors.warningInk }}>AI đang BẬT</span>
          ) : (
            <span style={{ color: colors.textSecondary }}>AI đang TẮT (mặc định)</span>
          )}
        </div>
        <div style={{ fontSize: 13, marginBottom: 4 }}>Muốn bật cần đủ 3 điều kiện:</div>
        <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13, lineHeight: 1.8 }}>
          <li>
            Khoá API trên máy chủ (OPENAI_API_KEY cho ảnh + chữ, ANTHROPIC_API_KEY cho chữ tiếng Việt) và danh sách
            MODERATION_IMAGE_PROVIDERS / MODERATION_TEXT_PROVIDERS.
            <div>
              <ProviderStatus settings={settings} />
            </div>
          </li>
          <li>
            Quy tắc đăng bài có câu về AI:{' '}
            {usingDefaultTerms ? (
              <span style={{ color: colors.successInk }}>đang dùng Quy tắc mặc định — máy chủ tự thêm câu AI khi bật.</span>
            ) : termsHasAiClause ? (
              <span style={{ color: colors.successInk }}>Quy tắc tự viết đã được đánh dấu có câu AI.</span>
            ) : (
              <span style={{ color: colors.dangerInk }}>
                Quy tắc tự viết CHƯA có câu AI — thêm ở phần "Điều khoản & trang HP Media" (máy chủ sẽ từ chối bật).
              </span>
            )}
          </li>
          <li>Sau khi bật, mọi người phải bấm Đồng ý lại Quy tắc mới trước khi đăng/bình luận tiếp.</li>
        </ol>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0 32px' }}>
        {switchItem('aiImagesEnabled', 'Soi ảnh và video', imageOk, aiImagesEnabled)}
        {switchItem('aiTextEnabled', 'Soi chữ của bài', textOk, aiTextEnabled)}
        {switchItem('aiCommentsEnabled', 'Soi bình luận', textOk, aiCommentsEnabled)}
      </div>

      <Form.Item
        label="Thứ tự nhà cung cấp — Ảnh & video"
        name="aiImageProviders"
        extra="Thứ tự chọn = thứ tự thử; nhà cung cấp đầu tiên trả lời được thì dùng kết quả đó."
      >
        <ProviderOrderInput envList={envImage} settings={settings} disabled={readOnly} />
      </Form.Item>
      <Form.Item label="Thứ tự nhà cung cấp — Chữ (bài và bình luận)" name="aiTextProviders">
        <ProviderOrderInput envList={envText} settings={settings} disabled={readOnly} />
      </Form.Item>

      <Form.Item
        label="Khi máy soi lỗi"
        name="aiFailMode"
        extra="Chọn 'giữ lại' khi đang có sự cố, vì khoá AI hết hạn sẽ làm mọi bài phải chờ duyệt."
      >
        <Radio.Group>
          <Space direction="vertical" size={4}>
            <Radio value="open">Máy soi lỗi thì vẫn đăng (khuyên dùng)</Radio>
            <Radio value="closed">Máy soi lỗi thì giữ lại chờ duyệt</Radio>
          </Space>
        </Radio.Group>
      </Form.Item>
      <Form.Item
        label="Số khung hình cắt ra từ mỗi video để soi"
        name="aiVideoFrames"
        rules={[{ type: 'number', min: 1, max: 20, message: 'Từ 1 đến 20.' }]}
      >
        <InputNumber min={1} max={20} addonAfter="khung" style={{ width: 160 }} />
      </Form.Item>

      <Form.Item label="Ngưỡng" name="aiThresholds">
        <ThresholdsInput disabled={readOnly} isDefault={settings.aiThresholdsIsDefault} />
      </Form.Item>

      {isAdmin && <TestImageBox />}
    </div>
  );
};
